import * as React from 'react';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-apollo';
import { makeStyles, Theme, createStyles, Typography } from '@material-ui/core';
import AnalyticsOutlinedIcon from '@material-ui/icons/EqualizerOutlined';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { KPISummaryQuery } from '../../../home/KPISummary/KPISummaryChart';
import { getSelectedItem, ChartFilter } from '../../../home/ChartComponents';
import { DrillDownType } from '../../../home/DrillDownType';
import { SearchCriteria } from '../../../home/SearchCriteria';
import { formatNumber } from '../../../helpers/ChartHelper';
import { useCurrencySymbol } from '../../../hooks/useCurrencySymbol';
import { UserFeature } from '../../../types/UserFeature';
import { SkeletonLoader } from '../../../components/loading/SkeletonLoader';
import { browserHistory } from '../../../components/AppContent';
import { linkToPage, showNotification } from '../../../App';
import EventSystem from '../../../EventSystem';
import { QUICK_VIEW_REFRESH_EVENT, useIsQuickViewPinned } from '../QuickViewDrawer';
import { RefreshingIndicator, runWithMinimumVisibleRefresh } from './RefreshingIndicator';

// This card's tint - this web app's own brand blue (theme.palette.primary.main /
// mainTheme.TemplateColor.Primary, src/index.tsx), matching YourEnquiriesWidget.tsx/
// YourTasksWidget.tsx's own re-tinted accent (all three read as one consistent colour).
const KPI_TINT = '#5173FF';
// A deeper stop of the same blue, purely for the icon chip/period-pill gradients below - kept
// as a second stop rather than a flat fill so this card's own chrome has some depth to it,
// same reasoning as the bar/track colours below.
const KPI_TINT_DEEP = '#3B5BE0';

// Brighter, more saturated red/amber/green than this widget's own original getIndicator
// literals (#a32d2d/#d98324/#0f6e56 - the app's shared --dc-error-fg-style corporate-report
// palette, still used as-is by the full KPI dashboard this card is a copy of). Explicit
// request ("the colours of the bars... looks dull") - kept local to this one glance card
// rather than changing the shared tokens, so the full dashboard's own report-style palette is
// untouched. Each tier also gets a soft tint twin, used for the bar's own track background and
// the row dot's halo, so a row reads as colour-coded as a whole rather than just its one dot.
const STATUS_RED = '#EF4444';
const STATUS_RED_SOFT = '#FEE2E2';
const STATUS_AMBER = '#F59E0B';
const STATUS_AMBER_SOFT = '#FEF3C7';
const STATUS_GREEN = '#10B981';
const STATUS_GREEN_SOFT = '#D1FAE5';

// A copy of home/KPISummary/KPISummaryChart.tsx - same query (KPISummaryQuery, exported from
// there for this reuse), same data (all 6 metrics x all 6 periods, same red/amber/green
// threshold logic, same bottom debt/unbilled totals), but with new UI/animations for the
// panel rather than that chart's plain MUI Table + RadioGroup: a period pill-switcher, each
// metric as a compact row, and the same gradient-wash/breathing-icon-chip/watermark chrome
// YourEnquiriesWidget.tsx/YourTasksWidget.tsx use. Deliberately dropped from the original:
// the video/AI-assistant/screenshot toolbar (chart-in-a-dashboard utilities, not a glance
// card's job) and per-row drilldown (onDrilldown/onTotalDrilldown feed a parent dashboard's
// own filter state via getSelectedItem - there's no such parent grid inside Quick View to
// receive it). The header itself still opens the full dashboard for anyone who wants the
// drilldowns.
type KPISummaryDataType = 'New Matters' | 'Non-Billable Hours' | 'Billable Hours' | 'Fees Recorded' | 'Fees Billed' | 'Fees Collected';

interface KpiPeriodFigures {
    actual: number | string | null;
    budget: number | string | null;
    percentage: number | string | null;
    // Unformatted twin of `percentage`, for the mini bar chart's bar width - kept separate
    // from the display string above so the chart never has to parse "83%" back into a number.
    rawPercentage: number | null;
    dot?: string;
    // Soft tint twin of `dot` (same tier, low-opacity) - the bar track's own background and
    // the row dot's halo, so both read as colour-coded to the same tier as the dot itself.
    dotSoft?: string;
}

interface KpiMetricRow {
    name: KPISummaryDataType;
    weekToDate: KpiPeriodFigures;
    monthToDate: KpiPeriodFigures;
    yearToDate: KpiPeriodFigures;
    previousMonthToDate: KpiPeriodFigures;
    previousMonthYearToDate: KpiPeriodFigures;
    forecast: KpiPeriodFigures;
}

interface KpiBottomRow {
    name: 'Total Debt' | 'Unbilled Fees' | 'Unbilled Disbursements';
    total: number;
    ninetyDaysTotal: number;
    ninetyDaysPercentage: number;
    exceedPercentage: number | null;
}

type DateOption = keyof Omit<KpiMetricRow, 'name'>;

const PERIOD_OPTIONS: { value: DateOption; label: string }[] = [
    { value: 'weekToDate', label: 'WTD' },
    { value: 'monthToDate', label: 'MTD' },
    { value: 'yearToDate', label: 'YTD' },
    { value: 'previousMonthToDate', label: 'Prev month' },
    { value: 'previousMonthYearToDate', label: 'Prev month YTD' },
    { value: 'forecast', label: 'Forecast' },
];

interface KpiMetricMtd {
    periodTotal: number;
    budget: number;
    percentage: number;
}
interface KpiCountMtd {
    periodTotal: number;
}
interface KpiTotal {
    periodTotal: number;
    ninetyDaysPercentage: number;
    ninetyDaysTotal: number;
}

interface KPISummaryQueryData {
    settings: {
        systems: {
            financialThresholdsSettings: {
                config: {
                    redIndicatorWhenActualVsBudgetIsLessThanPercentage: number | null;
                    amberIndicatorWhenActualVsBudgetIsLessThanPercentage: number | null;
                    redIndicatorWhenNinetyDayDebtExceedsPercentage: number | null;
                    redIndicatorWhenNinetyDayUnbilledFeesExceedsPercentage: number | null;
                    redIndicatorWhenNinetyDayUnbilledDisbursementsExceedsPercentage: number | null;
                };
            };
        };
    };
    statistics: {
        fees: {
            kpiSummary: {
                newMattersYTD: KpiCountMtd; newMattersMTD: KpiCountMtd; newMattersWTD: KpiCountMtd;
                newMattersPreviousMTD: KpiCountMtd; newMattersPreviousMYTD: KpiCountMtd;
                nonBillableHoursYTD: KpiCountMtd; nonBillableHoursMTD: KpiCountMtd; nonBillableHoursWTD: KpiCountMtd;
                nonBillableHoursPreviousMTD: KpiCountMtd; nonBillableHoursPreviousMYTD: KpiCountMtd;
                billableHoursYTD: KpiMetricMtd; billableHoursMTD: KpiMetricMtd; billableHoursWTD: KpiMetricMtd;
                billableHoursPreviousMTD: KpiMetricMtd; billableHoursPreviousMYTD: KpiMetricMtd;
                feesRecordedYTD: KpiMetricMtd; feesRecordedMTD: KpiMetricMtd; feesRecordedWTD: KpiMetricMtd;
                feesRecordedPreviousMTD: KpiMetricMtd; feesRecordedPreviousMYTD: KpiMetricMtd; feesRecordedForecast: KpiMetricMtd;
                feesBilledYTD: KpiMetricMtd; feesBilledMTD: KpiMetricMtd; feesBilledWTD: KpiMetricMtd;
                feesBilledPreviousMTD: KpiMetricMtd; feesBilledPreviousMYTD: KpiMetricMtd; feesBilledForecast: KpiMetricMtd;
                feesCollectedYTD: KpiMetricMtd; feesCollectedMTD: KpiMetricMtd; feesCollectedWTD: KpiMetricMtd;
                feesCollectedPreviousMTD: KpiMetricMtd; feesCollectedPreviousMYTD: KpiMetricMtd; feesCollectedForecast: KpiMetricMtd;
                totalDebtOwing: KpiTotal | null;
                totalUnbilledFees: KpiTotal | null;
                totalUnbilledDisbursements: KpiTotal | null;
            };
        };
    };
}

interface KPISummaryQueryParams {
    entityType: number | undefined;
    entityGuid: string | undefined;
    responsibility: number | undefined;
}

// entityType: 6 = the signed-in person themself for kpiSummary specifically - matches
// FeeEarnerDashboardPage.tsx's own default ("Always 6 (User) for Fee Earner Dashboard unless
// fee earner is selected then 5").
const KPI_SUMMARY_ENTITY_TYPE_SELF = 6;

function getIndicator(percentage: number, amberThreshold: number | null, redThreshold: number | null): { strong: string; soft: string } | undefined {
    if (!percentage || Math.round(percentage) === 0 || !amberThreshold || !redThreshold) {
        return undefined;
    }
    if (Math.round(percentage) <= redThreshold) {
        return { strong: STATUS_RED, soft: STATUS_RED_SOFT };
    } else if (Math.round(percentage) < amberThreshold) {
        return { strong: STATUS_AMBER, soft: STATUS_AMBER_SOFT };
    }
    return { strong: STATUS_GREEN, soft: STATUS_GREEN_SOFT };
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'var(--dc-surface)',
            // Matches the app's own standard card/table radius - see
            // YourEnquiriesWidget.tsx's identical comment.
            borderRadius: 8,
            padding: theme.spacing(2),
            // A layered "embossed" shadow instead of a plain hairline border - explicit
            // request ("I want the widgets to pop a bit... the border have some shadow, but
            // doesn't stand out"). Border dropped in favour of the shadow alone carrying the
            // card's own definition, same recipe as every other widget in this panel - see
            // YourEnquiriesWidget.tsx's identical comment.
            boxShadow: '0 1px 3px rgba(16,26,51,0.12), 0 6px 16px rgba(16,26,51,0.08)',
        },
        wash: {
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${KPI_TINT}29 0%, ${KPI_TINT}05 60%)`,
            pointerEvents: 'none',
        },
        watermark: {
            position: 'absolute',
            right: -22,
            top: -18,
            color: KPI_TINT,
            opacity: 0.1,
            pointerEvents: 'none',
            animation: '$watermarkBreathe 3400ms ease-in-out infinite alternate',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        },
        '@keyframes watermarkBreathe': {
            from: { transform: 'translateY(0)', opacity: 0.09 },
            to: { transform: 'translateY(-5px)', opacity: 0.14 },
        },
        content: {
            position: 'relative',
        },
        headerRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.25),
            marginBottom: theme.spacing(1.5),
            cursor: 'pointer',
        },
        iconChip: {
            width: 36,
            height: 36,
            minWidth: 36,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // A soft two-stop tint instead of one flat tint - same "soft chip" language as the
            // sibling cards' own icon chips (they stay a single flat tint, this is the one
            // deliberate bit of extra depth on this card, matching its own wash's diagonal).
            background: `linear-gradient(135deg, ${KPI_TINT}3d 0%, ${KPI_TINT}14 100%)`,
            color: KPI_TINT,
            animation: '$iconBreathe 2200ms ease-in-out infinite alternate',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        },
        '@keyframes iconBreathe': {
            from: { transform: 'scale(1)' },
            to: { transform: 'scale(1.12)' },
        },
        // The header chip's own animated glyph - explicit request to bring the same kind of
        // animated icon YourEnquiriesWidget.tsx's pulse-line/YourTasksWidget.tsx's check-draw
        // have to this card too. A small live equalizer (three bars growing/shrinking, staggered
        // so they don't move in lockstep) rather than a static AnalyticsOutlinedIcon - echoes
        // this card's own "actual vs budget" bar chart below, same as those two icons echo
        // their own widget's subject (a heartbeat pulse/a completing checkmark).
        kpiBar: {
            fill: KPI_TINT,
            transformBox: 'fill-box',
            transformOrigin: 'bottom',
            // Slower and on a smoother custom curve than the first pass (1400ms ease-in-out) -
            // explicit bug report, "not smooth... fast and looks jaggared" at that speed. Eased
            // in and out gently on both ends (rather than ease-in-out's sharper snap at a short
            // duration) so the scale change reads as a soft breathing motion, matching the
            // unhurried pace of this card's own iconBreathe/YourTasksWidget.tsx's checkDraw.
            animation: '$kpiBarPulse 2200ms cubic-bezier(0.45, 0, 0.55, 1) infinite alternate',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        },
        '@keyframes kpiBarPulse': {
            from: { transform: 'scaleY(0.45)' },
            to: { transform: 'scaleY(1)' },
        },
        heading: {
            fontWeight: 600,
            flex: 1,
        },
        openLink: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 26,
            height: 26,
            borderRadius: '50%',
            backgroundColor: `${KPI_TINT}24`,
            color: KPI_TINT,
        },
        periodRow: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            marginBottom: theme.spacing(1.5),
        },
        periodPill: {
            border: 'none',
            borderRadius: 999,
            padding: '4px 10px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            color: theme.palette.text.secondary,
            backgroundColor: 'transparent',
            transition: 'background-color 0.15s ease, color 0.15s ease',
            '&:hover': {
                backgroundColor: `${KPI_TINT}18`,
            },
        },
        periodPillActive: {
            background: `linear-gradient(135deg, ${KPI_TINT} 0%, ${KPI_TINT_DEEP} 100%)`,
            color: '#FFFFFF',
            boxShadow: `0 2px 8px ${KPI_TINT}4d`,
            '&:hover': {
                background: `linear-gradient(135deg, ${KPI_TINT} 0%, ${KPI_TINT_DEEP} 100%)`,
            },
        },
        // The card's one animated chart - a compact "actual vs budget" bar per metric that
        // has a budget for the selected period, standing in for the plain text rows below it
        // (which stay, for the exact figures a bar can't show). Bars grow in from 0 on mount
        // and re-animate to their new width whenever `period` changes - see AnimatedBudgetBar.
        chartLabel: {
            fontSize: 10.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: theme.palette.text.disabled,
            marginBottom: theme.spacing(1),
        },
        chart: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(0.75),
            marginBottom: theme.spacing(1.75),
            paddingBottom: theme.spacing(1.75),
            borderBottom: '1px solid var(--dc-border)',
        },
        chartRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        chartRowLabel: {
            width: 84,
            flexShrink: 0,
            fontSize: 11,
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        chartTrack: {
            position: 'relative',
            flex: 1,
            height: 8,
            borderRadius: 999,
            // Falls back to the old flat grey only if a row has no tier colour at all (rare -
            // getIndicator returns undefined only when thresholds/percentage aren't set) -
            // otherwise overridden per-row via inline style to a soft tint of that row's own
            // bar colour, so the whole row reads as one colour-coded unit instead of a
            // uniform grey track under every bar regardless of status.
            backgroundColor: 'var(--dc-surface-muted, #F1F2F6)',
            transition: 'background-color 0.2s ease',
        },
        chartFill: {
            height: '100%',
            borderRadius: 999,
            transition: 'width 700ms cubic-bezier(0.33, 1, 0.68, 1), background-color 0.2s ease',
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
            },
        },
        chartRowPercent: {
            width: 38,
            flexShrink: 0,
            textAlign: 'right',
            fontSize: 11.5,
            fontWeight: 700,
        },
        rows: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1.1),
        },
        row: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1),
        },
        // New Matters and the three exposure rows (Total Debt/Unbilled Fees/Unbilled
        // Disbursements) drill into Matter Summary Drill Down - explicit request, same
        // destination/mechanism (getSelectedItem) as the full KPI dashboard's own
        // KPISummaryChart.tsx and the mobile app's own FeeEarnerKpiSummaryChart.tsx. The other
        // five metrics stay plain/non-interactive, matching that same mobile precedent's own
        // reasoning - their web drilldowns go to a staff-billing/timesheet summary this panel
        // has no equivalent page for.
        rowClickable: {
            cursor: 'pointer',
            margin: theme.spacing(0, -1),
            padding: theme.spacing(0.4, 1),
            borderRadius: 8,
            transition: 'background-color 0.15s ease',
            '&:hover': {
                backgroundColor: 'var(--dc-surface-muted, #F1F2F6)',
            },
        },
        rowLabel: {
            fontSize: 13,
            color: theme.palette.text.primary,
        },
        rowValues: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        rowActual: {
            fontSize: 14,
            fontWeight: 700,
        },
        rowBudget: {
            fontSize: 11.5,
            color: theme.palette.text.secondary,
        },
        dot: {
            width: 8,
            height: 8,
            borderRadius: '50%',
        },
        divider: {
            borderTop: '1px solid var(--dc-border)',
            margin: theme.spacing(1.5, 0),
        },
        bottomLabel: {
            fontSize: 12.5,
            color: theme.palette.text.primary,
        },
        bottomValue: {
            fontSize: 12.5,
            fontWeight: 700,
        },
        bottomValueOver: {
            display: 'inline-flex',
            alignItems: 'center',
            color: STATUS_RED,
            backgroundColor: STATUS_RED_SOFT,
            padding: '2px 8px',
            borderRadius: 999,
        },
        emptyState: {
            fontSize: 13,
            color: theme.palette.text.secondary,
        },
    })
);

interface AnimatedBudgetBarProps {
    trackColor: string;
    fillGradient: string;
    widthPercentage: number;
}

// Grows a single "actual vs budget" bar in from 0 on mount, same two-nested-rAF technique as
// this file used to run at the whole-chart level. Pulled out into its own component and keyed
// by `period` at the call site below (chartMetrics.map) specifically so React remounts just
// this bar - not the whole row, not the whole chart - on every period switch: explicit bug
// report that switching tabs didn't reliably animate every bar, every time, even for a metric
// that had already rendered in a previous period. A plain state reset keyed off `period` in
// the parent (the original approach) still painted the OLD chartReady=true render with the NEW
// period's target width for one frame before the reset effect could run, which read as a
// flash-to-target-then-drop-to-0-then-regrow rather than one clean fill - remounting instead
// guarantees this bar's very first render is genuinely at 0%, nothing to flash from.
function AnimatedBudgetBar({ trackColor, fillGradient, widthPercentage }: AnimatedBudgetBarProps) {
    const classes = useStyles();
    const [isGrown, setIsGrown] = useState(false);
    const innerFrameRef = React.useRef<number | null>(null);
    React.useEffect(() => {
        const outerFrame = requestAnimationFrame(() => {
            innerFrameRef.current = requestAnimationFrame(() => setIsGrown(true));
        });
        return () => {
            cancelAnimationFrame(outerFrame);
            if (innerFrameRef.current !== null) {
                cancelAnimationFrame(innerFrameRef.current);
            }
        };
    // tslint:disable-next-line: align
    }, []);

    return (
        <div className={classes.chartTrack} style={{ backgroundColor: trackColor }}>
            <div className={classes.chartFill} style={{ width: isGrown ? `${widthPercentage}%` : '0%', background: fillGradient }} />
        </div>
    );
}

export const KpiSummaryWidget: React.FC = () => {
    const classes = useStyles();
    const currencyQuery = useCurrencySymbol();
    const [period, setPeriod] = useState<DateOption>('monthToDate');
    // Explicit request: this widget's own Matter Summary Drill Down navigation should stay in
    // the same tab while Quick View is docked (the whole point of docking is to keep working
    // in-page), but fall back to the existing new-tab behaviour every other chart's own
    // drilldown uses when Quick View is just a floating overlay - see onNewMattersDrilldown/
    // onExposureDrilldown's own openInSameTab argument below.
    const isQuickViewPinned = useIsQuickViewPinned();

    // Same skeleton-never-comes-back-on-refresh fix as YourEnquiriesWidget.tsx's own
    // hasLoadedHealthOnceRef - see that file's comment for why.
    const hasLoadedOnceRef = React.useRef(false);
    // Drives RefreshingIndicator - see YourEnquiriesWidget.tsx's own identical comment.
    const [isRefreshing, setIsRefreshing] = useState(false);

    // batchGroup opts this widget's query out of the default shared batch bucket - see
    // YourEnquiriesWidget.tsx's identical comment.
    const kpiQuery = useQuery<KPISummaryQueryData, KPISummaryQueryParams>(KPISummaryQuery, {
        variables: {
            entityType: KPI_SUMMARY_ENTITY_TYPE_SELF,
            entityGuid: UserFeature.UserId,
            responsibility: undefined,
        },
        context: { batchGroup: 'quickView-kpiSummary' },
    });

    React.useEffect(() => {
        if (kpiQuery.error) {
            showNotification('Failed to Fetch Your KPI\'s', kpiQuery.error.message, 'error');
        }
    // tslint:disable-next-line: align
    }, [kpiQuery.error]);

    // Quick View's own header Refresh button (QuickViewDrawer.tsx) - see
    // YourEnquiriesWidget.tsx's own identical comment for why refetchRef/the empty-deps
    // effect/the explicit cleanup are each needed. Only kpiQuery itself refetches - the
    // currency symbol is a tenant setting, not "data" this button is meant to refresh.
    const refetchRef = React.useRef(kpiQuery.refetch);
    refetchRef.current = kpiQuery.refetch;
    React.useEffect(() => {
        const onRefresh = () => {
            runWithMinimumVisibleRefresh(setIsRefreshing, () => refetchRef.current());
        };
        EventSystem.on(QUICK_VIEW_REFRESH_EVENT, onRefresh);
        return () => { EventSystem.event.removeListener(QUICK_VIEW_REFRESH_EVENT, onRefresh); };
    // tslint:disable-next-line: align
    }, []);

    const currencySymbol = currencyQuery.data?.options.currencySymbol.symbol ?? '$';

    // Same transformation KPISummaryChart.tsx's own useMemo does - see that file for the
    // reference this was copied from - just kept local here rather than extracted into a
    // shared hook, to avoid touching that file's own working internals for this reuse.
    const { metrics, bottomRows } = useMemo<{ metrics: KpiMetricRow[]; bottomRows: KpiBottomRow[] }>(() => {
        // Deliberately not keyed off kpiQuery.loading - that flips true again during a Quick
        // View header-button refresh's own refetch() (same flag the loading gate below used to
        // rely on), and gating this derivation on it too would have emptied out the already-
        // rendered card back to "Nothing to show" for that moment instead of leaving the
        // existing figures on screen until the refreshed ones replace them.
        if (kpiQuery.error || !kpiQuery.data) {
            return { metrics: [], bottomRows: [] };
        }

        const {
            redIndicatorWhenActualVsBudgetIsLessThanPercentage: redThreshold,
            amberIndicatorWhenActualVsBudgetIsLessThanPercentage: amberThreshold,
            redIndicatorWhenNinetyDayDebtExceedsPercentage,
            redIndicatorWhenNinetyDayUnbilledFeesExceedsPercentage,
            redIndicatorWhenNinetyDayUnbilledDisbursementsExceedsPercentage,
        } = kpiQuery.data.settings.systems.financialThresholdsSettings.config;

        const k = kpiQuery.data.statistics.fees.kpiSummary;

        const countFigures = (metric: KpiCountMtd): KpiPeriodFigures => ({
            actual: metric.periodTotal > 0 ? formatNumber(metric.periodTotal) : null,
            budget: null,
            percentage: null,
            rawPercentage: null,
        });

        const currencyFigures = (metric: KpiMetricMtd, withCurrency: boolean): KpiPeriodFigures => {
            const indicator = getIndicator(metric.percentage, amberThreshold, redThreshold);
            return {
                actual: metric.periodTotal > 0 ? `${withCurrency ? currencySymbol : ''}${formatNumber(metric.periodTotal)}` : null,
                budget: metric.budget > 0 ? `Bgt ${withCurrency ? currencySymbol : ''}${formatNumber(metric.budget)}` : null,
                percentage: metric.percentage > 0 ? `${formatNumber(metric.percentage)}%` : null,
                rawPercentage: metric.percentage > 0 ? metric.percentage : null,
                dot: indicator?.strong,
                dotSoft: indicator?.soft,
            };
        };

        const newMatters: KpiMetricRow = {
            name: 'New Matters',
            weekToDate: countFigures(k.newMattersWTD),
            monthToDate: countFigures(k.newMattersMTD),
            yearToDate: countFigures(k.newMattersYTD),
            previousMonthToDate: countFigures(k.newMattersPreviousMTD),
            previousMonthYearToDate: countFigures(k.newMattersPreviousMYTD),
            forecast: countFigures(k.newMattersYTD),
        };

        const billableHours: KpiMetricRow = {
            name: 'Billable Hours',
            weekToDate: currencyFigures(k.billableHoursWTD, false),
            monthToDate: currencyFigures(k.billableHoursMTD, false),
            yearToDate: currencyFigures(k.billableHoursYTD, false),
            previousMonthToDate: currencyFigures(k.billableHoursPreviousMTD, false),
            previousMonthYearToDate: currencyFigures(k.billableHoursPreviousMYTD, false),
            forecast: currencyFigures(k.billableHoursYTD, false),
        };

        const feesRecorded: KpiMetricRow = {
            name: 'Fees Recorded',
            weekToDate: currencyFigures(k.feesRecordedWTD, true),
            monthToDate: currencyFigures(k.feesRecordedMTD, true),
            yearToDate: currencyFigures(k.feesRecordedYTD, true),
            previousMonthToDate: currencyFigures(k.feesRecordedPreviousMTD, true),
            previousMonthYearToDate: currencyFigures(k.feesRecordedPreviousMYTD, true),
            forecast: currencyFigures(k.feesRecordedForecast, true),
        };

        const feesBilled: KpiMetricRow = {
            name: 'Fees Billed',
            weekToDate: currencyFigures(k.feesBilledWTD, true),
            monthToDate: currencyFigures(k.feesBilledMTD, true),
            yearToDate: currencyFigures(k.feesBilledYTD, true),
            previousMonthToDate: currencyFigures(k.feesBilledPreviousMTD, true),
            previousMonthYearToDate: currencyFigures(k.feesBilledPreviousMYTD, true),
            forecast: currencyFigures(k.feesBilledForecast, true),
        };

        const feesCollected: KpiMetricRow = {
            name: 'Fees Collected',
            weekToDate: currencyFigures(k.feesCollectedWTD, true),
            monthToDate: currencyFigures(k.feesCollectedMTD, true),
            yearToDate: currencyFigures(k.feesCollectedYTD, true),
            previousMonthToDate: currencyFigures(k.feesCollectedPreviousMTD, true),
            previousMonthYearToDate: currencyFigures(k.feesCollectedPreviousMYTD, true),
            forecast: currencyFigures(k.feesCollectedForecast, true),
        };

        const nonBillableHours: KpiMetricRow = {
            name: 'Non-Billable Hours',
            weekToDate: countFigures(k.nonBillableHoursWTD),
            monthToDate: countFigures(k.nonBillableHoursMTD),
            yearToDate: countFigures(k.nonBillableHoursYTD),
            previousMonthToDate: countFigures(k.nonBillableHoursPreviousMTD),
            previousMonthYearToDate: countFigures(k.nonBillableHoursPreviousMYTD),
            forecast: countFigures(k.nonBillableHoursYTD),
        };

        const bottomRowsBuilt: KpiBottomRow[] = [];
        if (k.totalDebtOwing) {
            bottomRowsBuilt.push({
                name: 'Total Debt', total: k.totalDebtOwing.periodTotal,
                ninetyDaysTotal: k.totalDebtOwing.ninetyDaysTotal, ninetyDaysPercentage: k.totalDebtOwing.ninetyDaysPercentage,
                exceedPercentage: redIndicatorWhenNinetyDayDebtExceedsPercentage,
            });
        }
        if (k.totalUnbilledFees) {
            bottomRowsBuilt.push({
                name: 'Unbilled Fees', total: k.totalUnbilledFees.periodTotal,
                ninetyDaysTotal: k.totalUnbilledFees.ninetyDaysTotal, ninetyDaysPercentage: k.totalUnbilledFees.ninetyDaysPercentage,
                exceedPercentage: redIndicatorWhenNinetyDayUnbilledFeesExceedsPercentage,
            });
        }
        if (k.totalUnbilledDisbursements) {
            bottomRowsBuilt.push({
                name: 'Unbilled Disbursements', total: k.totalUnbilledDisbursements.periodTotal,
                ninetyDaysTotal: k.totalUnbilledDisbursements.ninetyDaysTotal, ninetyDaysPercentage: k.totalUnbilledDisbursements.ninetyDaysPercentage,
                exceedPercentage: redIndicatorWhenNinetyDayUnbilledDisbursementsExceedsPercentage,
            });
        }

        return {
            metrics: [newMatters, billableHours, feesRecorded, feesBilled, feesCollected, nonBillableHours],
            bottomRows: bottomRowsBuilt,
        };
    // tslint:disable-next-line: align
    }, [kpiQuery.error, kpiQuery.data, currencySymbol]);

    const onOpenFeeEarnerDashboard = () => {
        browserHistory.push(linkToPage('feeEarnerDashboard'));
    };

    // Same chartFilter shape KPISummaryChart.tsx's own query/drilldowns use for this exact
    // entity (see this file's own kpiQuery variables above) - keeps the drilldown scoped to
    // "my own" matters, matching what's actually shown here, rather than widening to firm-wide
    // the moment someone drills in.
    const chartFilter: ChartFilter = {
        entityType: KPI_SUMMARY_ENTITY_TYPE_SELF,
        entityGuid: UserFeature.UserId,
        responsibility: undefined,
    };

    const getPeriodTitle = (selectedPeriod: DateOption): string => {
        switch (selectedPeriod) {
            case 'monthToDate': return 'Month to Date';
            case 'previousMonthToDate': return 'Previous Month';
            case 'yearToDate': return 'Year to Date';
            case 'previousMonthYearToDate': return 'Previous Month YTD';
            case 'forecast': return 'Full Year Budget Forecast';
            case 'weekToDate': return 'Week to Date';
            default: return 'Month to Date';
        }
    };

    // Exact same criteria as KPISummaryChart.tsx's own getNewMattersCriteria - New Matters is
    // the one metric row mobile's own FeeEarnerKpiSummaryChart.tsx also makes interactive
    // (see that file's own header comment); the other five stay plain here for the same
    // reason mobile gives - their own web drilldowns target a staff-billing/timesheet summary
    // this panel has no equivalent page for.
    const getNewMattersCriteria = (selectedPeriod: DateOption): SearchCriteria[] => {
        switch (selectedPeriod) {
            case 'monthToDate': return [{ comparison: 'curr', operator: 'instruction', property: 'MonthToDate' }];
            case 'previousMonthToDate': return [{ comparison: 'prev', operator: 'instruction', property: 'ThisMonth' }];
            case 'yearToDate': return [{ comparison: 'curr', operator: 'instruction', property: 'YearToDate' }];
            case 'previousMonthYearToDate': return [{ comparison: 'pmytd', operator: 'instruction', property: 'MonthToDate' }];
            case 'forecast': return [{ comparison: 'curr', operator: 'instruction', property: 'YearToDate' }];
            case 'weekToDate': return [{ comparison: 'curr', operator: 'instruction', property: 'WeekToDate' }];
            default: return [{ comparison: 'curr', operator: 'instruction', property: 'MonthToDate' }];
        }
    };

    const onNewMattersDrilldown = () => {
        getSelectedItem(
            chartFilter,
            browserHistory,
            getNewMattersCriteria(period),
            true,
            `KPI Summary - New Matters for ${getPeriodTitle(period)}`,
            DrillDownType.matterSummaryDrilldown,
            'New Matter Details',
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            isQuickViewPinned
        );
    };

    // Exact same criteria/defaultView as KPISummaryChart.tsx's own onTotalDrilldown - all
    // three exposure rows drill in here (not just New Matters), matching mobile's own
    // FeeEarnerKpiSummaryChart.tsx exactly ("the three exposure rows drill into
    // MatterListScreen's matter-summary drilldown").
    const onExposureDrilldown = (rowName: KpiBottomRow['name']) => () => {
        const searchCriterias: SearchCriteria[] = [];
        let defaultView = '';
        if (rowName === 'Total Debt') {
            searchCriterias.push({ property: 'DebtOwing', operator: 'not', comparison: '0' });
            searchCriterias.push({ property: 'DebtOwing', operator: 'isnot', comparison: 'null' });
            defaultView = 'Debtors';
        } else if (rowName === 'Unbilled Fees') {
            searchCriterias.push({ property: 'TotalWIPBalanceExTax', operator: 'not', comparison: '0' });
            searchCriterias.push({ property: 'TotalWIPBalanceExTax', operator: 'isnot', comparison: 'null' });
            defaultView = 'Unbilled Fees';
        } else {
            searchCriterias.push({ property: 'UnbilledDisbursements', operator: 'gt', comparison: '0' });
            defaultView = 'Matters';
        }
        getSelectedItem(
            chartFilter,
            browserHistory,
            searchCriterias,
            true,
            `KPI Summary - ${rowName}`,
            DrillDownType.matterSummaryDrilldown,
            defaultView,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            isQuickViewPinned
        );
    };

    if (kpiQuery.data) {
        hasLoadedOnceRef.current = true;
    }

    if (!hasLoadedOnceRef.current && (kpiQuery.loading || currencyQuery.loading)) {
        return (
            <div className={classes.root}>
                <SkeletonLoader variant="panel" columns={1} rows={4} />
            </div>
        );
    }

    if (kpiQuery.error || metrics.length === 0) {
        return null;
    }

    // Same exclusion/visibility rules KPISummaryChart.tsx's own render applies.
    const excludedForForecast: KPISummaryDataType[] = ['New Matters', 'Billable Hours', 'Non-Billable Hours'];
    const visibleMetrics = metrics
        .filter((metric) => period === 'forecast' ? !excludedForForecast.includes(metric.name) : true)
        .filter((metric) => {
            if (metric.name === 'Fees Collected' || metric.name === 'Non-Billable Hours') {
                return !!metric[period].actual || !!metric[period].budget;
            }
            return true;
        });

    // Only the metrics with an actual budget to measure against for this period - New
    // Matters/Non-Billable Hours are plain counts with no percentage figure to chart.
    const chartMetrics = visibleMetrics.filter((metric) => metric[period].rawPercentage !== null);

    return (
        <div className={classes.root}>
            <div className={classes.wash} />
            <AnalyticsOutlinedIcon className={classes.watermark} style={{ fontSize: 104 }} />
            <div className={classes.content}>
                <div
                    className={classes.headerRow}
                    onClick={onOpenFeeEarnerDashboard}
                    role="button"
                    tabIndex={0}
                    aria-label="Open your full KPI dashboard"
                    onKeyDown={(e) => { if (e.key === 'Enter') { onOpenFeeEarnerDashboard(); } }}
                >
                    <span className={classes.iconChip}>
                        <svg width={20} height={20} viewBox="0 0 24 24" style={{ overflow: 'visible' }}>
                            <rect x={2} y={10} width={4} height={12} rx={1} className={classes.kpiBar} style={{ animationDelay: '0ms' }} />
                            <rect x={10} y={4} width={4} height={18} rx={1} className={classes.kpiBar} style={{ animationDelay: '250ms' }} />
                            <rect x={18} y={13} width={4} height={9} rx={1} className={classes.kpiBar} style={{ animationDelay: '500ms' }} />
                        </svg>
                    </span>
                    <Typography variant="subtitle2" className={classes.heading}>Your KPI's</Typography>
                    {isRefreshing && <RefreshingIndicator />}
                    <span className={classes.openLink}>
                        <ArrowForwardIcon style={{ fontSize: 14 }} />
                    </span>
                </div>

                <div className={classes.periodRow}>
                    {PERIOD_OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            className={`${classes.periodPill} ${period === option.value ? classes.periodPillActive : ''}`}
                            onClick={() => setPeriod(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>

                {chartMetrics.length > 0 && (
                    <>
                        <Typography className={classes.chartLabel}>Actual vs budget</Typography>
                        <div className={classes.chart}>
                            {chartMetrics.map((metric) => {
                                const figures = metric[period];
                                const rawPercentage = figures.rawPercentage ?? 0;
                                const barColor = figures.dot ?? KPI_TINT;
                                const barTrackColor = figures.dotSoft ?? `${barColor}1f`;
                                const barWidth = Math.min(rawPercentage, 100);
                                return (
                                    <div key={metric.name} className={classes.chartRow}>
                                        <Typography className={classes.chartRowLabel}>{metric.name}</Typography>
                                        {/* Keyed by period (not just metric.name) so this bar remounts - and so
                                            genuinely restarts its own grow-from-0 animation - on every period
                                            switch, even for a metric that already had a bar in the previous
                                            period. See AnimatedBudgetBar's own comment for why remounting, not a
                                            plain state reset, was needed to make this reliably smooth every time. */}
                                        <AnimatedBudgetBar
                                            key={period}
                                            trackColor={barTrackColor}
                                            fillGradient={`linear-gradient(90deg, ${barColor}a8 0%, ${barColor} 100%)`}
                                            widthPercentage={barWidth}
                                        />
                                        <Typography className={classes.chartRowPercent} style={{ color: barColor }}>
                                            {Math.round(rawPercentage)}%
                                        </Typography>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {visibleMetrics.length > 0 ? (
                    <div className={classes.rows}>
                        {visibleMetrics.map((metric) => {
                            const figures = metric[period];
                            const isDrillable = metric.name === 'New Matters';
                            return (
                                <div
                                    key={metric.name}
                                    className={`${classes.row} ${isDrillable ? classes.rowClickable : ''}`}
                                    onClick={isDrillable ? onNewMattersDrilldown : undefined}
                                    role={isDrillable ? 'button' : undefined}
                                    tabIndex={isDrillable ? 0 : undefined}
                                    onKeyDown={isDrillable ? (e) => { if (e.key === 'Enter') { onNewMattersDrilldown(); } } : undefined}
                                >
                                    <Typography className={classes.rowLabel}>{metric.name}</Typography>
                                    <div className={classes.rowValues}>
                                        {figures.budget && <Typography className={classes.rowBudget}>{figures.budget}</Typography>}
                                        <Typography className={classes.rowActual}>{figures.actual ?? '-'}</Typography>
                                        {figures.dot && (
                                            <span
                                                className={classes.dot}
                                                style={{ backgroundColor: figures.dot, boxShadow: `0 0 0 3px ${figures.dotSoft ?? 'transparent'}` }}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <Typography className={classes.emptyState}>Nothing to show for this period.</Typography>
                )}

                {bottomRows.length > 0 && (
                    <>
                        <div className={classes.divider} />
                        <div className={classes.rows}>
                            {bottomRows.map((row) => {
                                const isOver = !!row.exceedPercentage && Math.round(row.ninetyDaysPercentage) >= row.exceedPercentage;
                                const onDrilldown = onExposureDrilldown(row.name);
                                return (
                                    <div
                                        key={row.name}
                                        className={`${classes.row} ${classes.rowClickable}`}
                                        onClick={onDrilldown}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { onDrilldown(); } }}
                                    >
                                        <Typography className={classes.bottomLabel}>{row.name}</Typography>
                                        <Typography className={`${classes.bottomValue} ${isOver ? classes.bottomValueOver : ''}`}>
                                            {currencySymbol}{formatNumber(row.total)}
                                        </Typography>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
